/**
 * Plate Buddies Journal — seed posts.
 *
 * Each post is a plain JS object so we don't need a CMS. To publish a new
 * one, add another entry at the top of POSTS. The array is rendered oldest
 * → newest based on `published_at`.
 *
 * Body is a mini structured DSL (an array of blocks). Types:
 *   { type: "p", text }         — paragraph (may contain <b>, <i>, <a href>)
 *   { type: "h2", text }        — section heading
 *   { type: "h3", text }        — sub-heading
 *   { type: "quote", text, by } — pull quote
 *   { type: "list", items: [] } — bulleted list
 *   { type: "num", items: [] }  — numbered list
 *   { type: "callout", title, text, tone: "coral"|"aqua"|"sun" }
 *   { type: "cta", href, label, sub } — inline CTA card
 *   { type: "image", src, alt, caption }
 *
 * Fields for SEO / rendering:
 *   slug         — URL segment (/journal/{slug})
 *   title        — H1 + <title>
 *   subtitle     — deck below the H1
 *   excerpt      — 150-160 char preview shown on /journal + used as meta description
 *   hero_image   — full URL to a wide 16:9 or 3:2 hero image
 *   hero_alt     — alt text for accessibility + Google image search
 *   category     — one of "food exploration", "meal planning", "screen-free"
 *   author       — display name
 *   published_at — ISO date
 *   read_time_min — estimated minutes
 *   tags         — array of tag strings (rendered as chips)
 *   target_keyword — the long-tail keyword this post is optimized for (for our records)
 */

export const POSTS = [
  {
    slug: "how-to-get-toddler-to-try-new-foods",
    title: "How to Get Your Toddler to Try New Foods (The 5-Bite Trick)",
    subtitle:
      "A gentle, neuroscience-backed method that helps a 3-year-old warm up to broccoli — without threats, bribes, or the airplane spoon.",
    excerpt:
      "The 5-bite trick, the 15-exposure rule, and a gentle phrase for the moment your toddler pushes the plate away. A parent-tested playbook that actually feels doable.",
    hero_image:
      "https://images.unsplash.com/photo-1476124369491-e7addf5db371?auto=format&fit=crop&w=1600&q=70",
    hero_alt: "Toddler in a high chair curiously reaching for a new food",
    category: "food exploration",
    author: "The Plate Buddies Team",
    published_at: "2026-03-03",
    read_time_min: 7,
    tags: ["toddlers", "new foods", "food exposure", "exploration"],
    target_keyword: "how to get toddler to try new foods",
    body: [
      {
        type: "p",
        text:
          "Here's a gentle truth about toddlers and new food: <b>the average child needs 10 to 15 neutral exposures before accepting a new food</b>. Most of us understandably stop trying after 3 (repetition is exhausting when the fifteenth green bean gets thrown on the floor). But that's the whole gap. Not willpower, not clever recipes — just repetition without pressure.",
      },
      {
        type: "p",
        text:
          "This post is the protocol we've used with hundreds of families. It's a 7-minute read. Try it for four weeks and there's a good chance you'll see a real shift in how your kid approaches new food.",
      },
      { type: "h2", text: "Why your toddler refuses everything (it's not personal)" },
      {
        type: "p",
        text:
          "Between 18 months and 3 years, most children move through <b>neophobia</b> — a hard-wired wariness of new foods. It's evolutionary: a toddler who ate every berry they saw wouldn't have made it past age 4. Refusing broccoli is biology, not stubbornness.",
      },
      {
        type: "p",
        text:
          "The good news: neophobia is temporary and softens naturally between ages 4 and 6. Your job for now isn't to \"win.\" It's to keep offering the food in a low-pressure way so that when their brain is ready, the food already feels familiar.",
      },
      {
        type: "callout",
        tone: "aqua",
        title: "The science, briefly",
        text:
          "A 2003 study in Appetite tracked toddlers offered a novel vegetable 8 times over 2 weeks. Acceptance rose from 20% to 78% — with no coercion, no rewards, and no plate-cleaning. Repeated neutral exposure is the whole intervention.",
      },
      { type: "h2", text: "The 5-Bite Trick" },
      {
        type: "p",
        text:
          "This is the daily practice. It takes 30 seconds per meal. It works with kids from age 2.",
      },
      {
        type: "num",
        items: [
          "<b>Put a small portion of one new food on the plate.</b> A single floret of broccoli. Two green beans. One slice of cucumber. Any more can look like a challenge.",
          "<b>Say one sentence, calmly.</b> \"This is on your plate. You can try it or not.\" Then let it be.",
          "<b>Offer 5 different ways to interact with the food.</b> Look at it. Touch it. Smell it. Kiss it. Lick it. Each interaction counts as an \"exposure.\" A kid who kissed a green bean five times has done more work than a kid who was pressured to eat one.",
          "<b>Model eating it yourself, quietly.</b> Take a bite in front of them. Say nothing. Chew. Look content. Kids watch far more than they listen.",
          "<b>End the meal without commentary.</b> No celebrating the bite. No punishing the refusal. \"Dinner's over — I hope you have a lovely evening\" is a complete sentence.",
        ],
      },
      { type: "h2", text: "The phrases that quietly work against us" },
      {
        type: "p",
        text:
          "These next few feel supportive but can accidentally turn food into a performance:",
      },
      {
        type: "list",
        items: [
          "\"Just one bite for Mommy.\" (Turns the food into an emotional transaction.)",
          "\"If you finish your peas you can have dessert.\" (Signals peas are the toll and dessert is the reward — the opposite of what we want.)",
          "\"Come on, you loved this last week!\" (Kids dig in when they feel gently cornered.)",
          "\"Try it, I promise you'll like it.\" (Puts your credibility on the line — some kids will pass just to make the promise wrong.)",
          "\"Are you sure you don't want to try?\" (Signals the answer matters. Neutral means neutral.)",
        ],
      },
      { type: "h2", text: "The one phrase to memorize" },
      {
        type: "quote",
        text:
          "\"You don't have to eat it. It's here if you want it.\"",
      },
      {
        type: "p",
        text:
          "Say this and mean it. The child's autonomy over their body is the most important thing they'll learn from your dinner table. That control is what lets them, eventually, take the bite.",
      },
      { type: "h2", text: "The character-play upgrade" },
      {
        type: "p",
        text:
          "Everything above works. This next part makes it work faster.",
      },
      {
        type: "p",
        text:
          "Kids will try foods for characters that they won't try for parents. Give the food a name and a story. \"This is Broccoli Boomer. He's brave. He's yours if you can be brave enough to touch him.\" We watched a 3-year-old who had refused broccoli for a year kiss a floret named Boomer within 2 minutes.",
      },
      {
        type: "p",
        text:
          "This is exactly why <a href=\"/free-buddy\">Plate Buddies</a> exists. Your kid designs their own food character, and suddenly they're eating what the Buddy eats. The Buddy has opinions. The Buddy is brave. The Buddy needs your kid to help finish the plate. Copy the trick even without our tool — invent a character for tonight's vegetable and see what happens.",
      },
      {
        type: "cta",
        href: "/free-buddy",
        label: "Design your kid's food character — free",
        sub: "The 30-second trick that makes new foods 3× easier to try.",
      },
      { type: "h2", text: "The 4-week exposure calendar" },
      {
        type: "p",
        text:
          "Pick <b>one new food per week</b>. Serve it at 5 dinners that week — same food, tiny portion, no pressure. Track it on a paper calendar on the fridge. Kids love checking boxes even when they didn't eat the thing.",
      },
      {
        type: "list",
        items: [
          "<b>Week 1 — Green vegetable.</b> Broccoli, green beans, or peas. Start with the one that scares your kid least.",
          "<b>Week 2 — Orange vegetable.</b> Carrot sticks, roasted sweet potato, or bell pepper. Sweet vegetables are the gateway.",
          "<b>Week 3 — New protein.</b> Fish, eggs, or beans. Small portion. Same 5-bite trick.",
          "<b>Week 4 — A texture challenge.</b> A crunchy raw thing, or a mushy cooked thing — whichever your kid usually refuses.",
        ],
      },
      { type: "h2", text: "What to do when they gag or spit it out" },
      {
        type: "p",
        text:
          "Stay soft. \"You can spit it into a napkin\" is a great line. Skip the \"you have to apologize\" part. Skip the forced swallow. Gagging is a real physiological response — meeting it with calm helps kids learn to trust their bodies, and that trust lasts a lifetime.",
      },
      {
        type: "p",
        text:
          "Persistent gagging across a wide range of foods, plus weight loss or fewer than 20 accepted foods, is worth a gentle mention to your pediatrician — sometimes there's an oral-motor or sensory piece that a feeding therapist can help with in just a few sessions.",
      },
      { type: "h2", text: "The long game" },
      {
        type: "p",
        text:
          "The kid you're feeding today isn't the kid you'll be feeding at eight. The kid at eight will eat things you can't imagine right now (they'll also complain about them — but that's another post). Your only job is to keep the table calm, keep the exposures neutral, and let dinner stop being about willpower. That's really the whole thing. Everything else is patience — served with a side of humor.",
      },
    ],
  },

  {
    slug: "family-dinner-conversation-ideas-kids-will-answer",
    title: "50 Family Dinner Conversation Ideas Kids Will Actually Answer",
    subtitle:
      "\"How was school?\" gets you \"fine.\" These 50 gentle prompts get you their whole day — and work with kids from age 3 to 13.",
    excerpt:
      "Instead of \"how was your day\" — 50 warm dinner-conversation prompts kids actually want to answer, from silly (would you rather) to soulful (rose-bud-thorn).",
    hero_image:
      "https://images.unsplash.com/photo-1550989460-0adf9ea622e2?auto=format&fit=crop&w=1600&q=70",
    hero_alt: "Family laughing around a dinner table sharing stories",
    category: "screen-free",
    author: "The Plate Buddies Team",
    published_at: "2026-02-27",
    read_time_min: 6,
    tags: ["conversation", "family dinner", "connection", "screen-free"],
    target_keyword: "family dinner conversation ideas",
    body: [
      {
        type: "p",
        text:
          "The average family gets less than 15 minutes a day of undistracted conversation with their kids. Dinner is the biggest opportunity — and \"how was school?\" is the biggest missed opportunity.",
      },
      {
        type: "p",
        text:
          "The problem with \"how was your day\" is that it's a yes/no question wrapped in a fake open question. Kids answer \"fine\" because they're already at \"fine\" and there's no on-ramp to more. These 50 prompts have on-ramps.",
      },
      {
        type: "callout",
        tone: "coral",
        title: "The one gentle rule for all 50",
        text:
          "Answer the prompt yourself first. Kids often won't share their embarrassing moment until they see you share yours. Model the vulnerability you'd love to see back.",
      },
      { type: "h2", text: "The 10 warm-ups (ages 3+)" },
      {
        type: "p",
        text:
          "Start every dinner with one of these. Fast, low-stakes, gets everyone talking.",
      },
      {
        type: "num",
        items: [
          "What was the best thing you ate today?",
          "Who made you laugh today?",
          "What's one thing you learned that you didn't know yesterday?",
          "If today had a color, what color would it be?",
          "What was the loudest thing you heard today?",
          "Did anything surprise you today — good or bad?",
          "Who did you sit next to at lunch/circle time?",
          "What was the hardest part of your day?",
          "What's one thing you're proud of from today?",
          "If you could rewind one moment from today, which one?",
        ],
      },
      { type: "h2", text: "The 10 rose-bud-thorn variations (ages 4+)" },
      {
        type: "p",
        text:
          "The classic \"one high, one low, one thing you're looking forward to\" — remixed 10 ways so it stays fresh.",
      },
      {
        type: "num",
        items: [
          "Rose · Bud · Thorn — one good, one exciting, one hard.",
          "Peak · Pit · Pit-stop — best moment, worst moment, funniest moment.",
          "Sunshine · Storm · Rainbow — the same three feelings, weather-flavored.",
          "One win. One flop. One thing to try tomorrow.",
          "Something I did. Something I saw. Something I felt.",
          "A moment I want to remember. A moment I want to forget.",
          "A moment I was brave. A moment I was afraid.",
          "Something small that mattered.",
          "Something I noticed that nobody else did.",
          "One person I want to thank tomorrow — and for what.",
        ],
      },
      {
        type: "cta",
        href: "/free-buddy",
        label: "Design a Plate Buddy — free",
        sub: "Give your kid a food character to talk about tonight. Ages 2–7.",
      },
      { type: "h2", text: "The 10 \"would you rather\" prompts (ages 5+)" },
      {
        type: "p",
        text:
          "Silly, safe, endless. Great for the kid who \"has nothing to say.\" Every answer requires a reason — that's where the conversation lives.",
      },
      {
        type: "num",
        items: [
          "Would you rather have a pet dragon or a pet dinosaur?",
          "Would you rather be invisible or be able to fly?",
          "Would you rather live in a treehouse or in a submarine?",
          "Would you rather eat pizza every day for a year, or ice cream every day for a year?",
          "Would you rather be able to talk to animals or read minds?",
          "Would you rather always have to sing instead of talking, or dance everywhere you walk?",
          "Would you rather be famous for something amazing, or be famous for something embarrassing?",
          "Would you rather have 10 hands or 10 feet?",
          "Would you rather be able to teleport or time-travel?",
          "Would you rather be the world's best at something no one cares about, or okay at something everyone loves?",
        ],
      },
      { type: "h2", text: "The 10 big questions (ages 6+)" },
      {
        type: "p",
        text:
          "For the older kid who's ready for a little depth. Ask one at a time. Give them a full minute to think. Try to let the silence sit — most kids fill it eventually.",
      },
      {
        type: "num",
        items: [
          "What's something you believe now that you didn't believe last year?",
          "Who do you think has the hardest job in our family?",
          "If you could change one rule at your school, which one?",
          "What's the kindest thing anyone's ever done for you?",
          "If you got a whole day off, no plans, no screens, no siblings — what would you do?",
          "Who's someone you used to be friends with, and what happened?",
          "What's a thing you're really good at that nobody knows?",
          "If you could give one gift to the whole world, what would it be?",
          "What's one thing you wish grown-ups understood about being a kid?",
          "What do you think our family is best at, together?",
        ],
      },
      { type: "h2", text: "The 10 memory-makers (any age)" },
      {
        type: "p",
        text:
          "These build family lore. Ask them once a season — you'll be quoting the answers for the rest of their childhood.",
      },
      {
        type: "num",
        items: [
          "What's your earliest memory?",
          "What's the funniest thing that ever happened to us as a family?",
          "What's a story from before you were born that you love hearing?",
          "What's a family tradition you'd want to keep when you're a grown-up?",
          "What's one thing you'll never forget about this house?",
          "If we made a movie about our family, what would we call it?",
          "What's a smell that reminds you of home?",
          "What's the best meal you've ever had — and who cooked it?",
          "What's the bravest thing you've ever done?",
          "What's one thing you hope you're still doing 20 years from now?",
        ],
      },
      {
        type: "callout",
        tone: "sun",
        title: "Save the good ones",
        text:
          "Keep a small notebook (or a note on your phone) called \"Dinner Table Quotes.\" Write down the sentences your kid says that make you laugh or cry. In 10 years this is the most valuable thing you own.",
      },
      { type: "h2", text: "How to make the prompts stick" },
      {
        type: "p",
        text:
          "Print this list (or screenshot it — no judgment). Tape it inside a kitchen cabinet. Try one prompt per night — same time, same order (warm-up on weeknights → rose-bud-thorn → sillies on weekends). Over time the kid stops asking \"what's for dinner\" and starts asking \"what's tonight's question?\"",
      },
      {
        type: "p",
        text:
          "That's the whole gentle trick. Give the meal a shape. Give the conversation an on-ramp. Then sit back and watch what your kid was quietly thinking about all day come pouring out.",
      },
      {
        type: "quote",
        text:
          "The kids who talk to their parents at 15 are the kids whose parents kept asking questions at 5 — even when the answers were nonsense.",
      },
    ],
  },

  {
    slug: "family-dinner-ideas-that-actually-work",
    title: "10 Family Dinner Ideas That Actually Work (Without Bribes)",
    subtitle:
      "A five-minute playbook for turning dinner from a battle into a little adventure — no clean-plate club required.",
    excerpt:
      "Real, tested strategies from real parents. Character play, plating tricks, and 10 dinner ideas that quietly get the vegetables in — no clean-plate club needed.",
    hero_image:
      "https://images.unsplash.com/photo-1544025162-d76694265947?auto=format&fit=crop&w=1600&q=70",
    hero_alt: "Colorful family dinner spread with kid-friendly plating",
    category: "food exploration",
    author: "The Plate Buddies Team",
    published_at: "2026-02-24",
    read_time_min: 8,
    tags: ["toddlers", "dinner ideas", "family dinner", "food exploration"],
    target_keyword: "family dinner ideas kids",
    body: [
      {
        type: "p",
        text:
          "If you're reading this at 4:47 PM with a fridge full of food a small human refuses to eat — take a breath. You're not failing. Your child isn't broken. And you don't need another sticker chart.",
      },
      {
        type: "p",
        text:
          "Selective eating is developmentally normal between ages 2 and 7 — the same window where a child is figuring out control, autonomy, and taste. Force-feeding tends to backfire. Bribery does too — kids learn the game quickly and raise their price. What often helps is <b>reframing dinner from a compliance test into a low-stakes little adventure</b>.",
      },
      {
        type: "p",
        text:
          "Here's the gentle framework we use, followed by 10 dinner ideas you can borrow tonight.",
      },
      { type: "h2", text: "The three rules that changed our dinners" },
      {
        type: "num",
        items: [
          "<b>You cook it. They decide how much.</b> Parents choose what's for dinner and when. Kids decide how much (including, some nights, none). This is Ellyn Satter's division of responsibility — the most well-researched approach to raising a confident little eater.",
          "<b>Story beats broccoli.</b> Kids won't always reach for a vegetable — but they'll almost always reach for what their favorite character is eating. Sometimes what we call the food matters as much as the food itself.",
          "<b>Same food, five times, no comment.</b> On average it takes about 10–15 <i>neutral</i> exposures for a toddler to warm to a new food. Serve it, don't announce it, don't make it a Moment when they try it. Just quietly keep offering.",
        ],
      },
      {
        type: "callout",
        tone: "coral",
        title: "The bribe trap",
        text:
          "\"One more bite and you can have a cookie\" quietly teaches kids that dinner is the tax they pay for dessert. It might work tonight — but over time it usually makes food battles trickier, not easier. This one's okay to leave out of the toolkit.",
      },
      { type: "h2", text: "10 dinner ideas that make exploration easy" },
      { type: "h3", text: "1. Rainbow skewers" },
      {
        type: "p",
        text:
          "Cubed chicken, bell pepper chunks, cherry tomatoes, and pineapple on a wooden skewer. Kids will eat anything on a stick. Grill or oven-roast at 400°F for 15 minutes.",
      },
      { type: "h3", text: "2. Build-your-own pasta bar" },
      {
        type: "p",
        text:
          "Cook the pasta. Put out three toppings: butter + parmesan, marinara, and a green (peas, spinach). Everyone builds their own bowl. The illusion of choice is dinner magic.",
      },
      { type: "h3", text: "3. Waffle sandwiches" },
      {
        type: "p",
        text:
          "Two frozen waffles + a slice of turkey + cheese + honey mustard. Cook in a panini press or a hot pan. Sweet-savory beats \"is this dinner or breakfast?\" every time.",
      },
      { type: "h3", text: "4. Tiny tacos with the fillings on the side" },
      {
        type: "p",
        text:
          "Serve mini tortillas, seasoned ground beef, cheese, corn, and diced avocado in separate bowls. The kid builds. You didn't fight anyone about tomatoes.",
      },
      { type: "h3", text: "5. Sheet-pan sausage + veggies" },
      {
        type: "p",
        text:
          "Sliced chicken sausage + potatoes + broccoli tossed with olive oil, salt, and paprika. Roast at 425°F for 22 minutes. The veggies caramelize (read: taste like candy).",
      },
      { type: "h3", text: "6. Homemade pizza night" },
      {
        type: "p",
        text:
          "Store-bought naan or pita as the base. Sauce, mozzarella, whatever toppings each family member wants. The kid who \"hates\" mushrooms suddenly loves the mushroom they placed themselves.",
      },
      { type: "h3", text: "7. Egg-in-a-hole" },
      {
        type: "p",
        text:
          "Cut a heart or star out of a slice of bread. Butter the pan, drop the bread in, crack an egg into the hole. Add a side of berries and turkey bacon and dinner is served in 6 minutes.",
      },
      { type: "h3", text: "8. Chicken \"nuggets\" made from real chicken" },
      {
        type: "p",
        text:
          "Cubed chicken breast, dredged in flour → egg → panko + parmesan. Bake at 425°F for 12 minutes. Served with 3 dipping sauces the kid gets to name.",
      },
      { type: "h3", text: "9. Rice bowl with the ingredients separated" },
      {
        type: "p",
        text:
          "Rice, teriyaki chicken, edamame, and cucumber slices, each in its own quadrant of the plate. Toddlers hate mixing. Respect the segregation.",
      },
      { type: "h3", text: "10. Soup night with a build-your-own topping bar" },
      {
        type: "p",
        text:
          "One pot of simple chicken-noodle or tomato soup. Toppings: crackers, shredded cheese, croutons, a squeeze of lemon. The toppings are the whole point.",
      },
      { type: "h2", text: "The character play trick" },
      {
        type: "p",
        text:
          "Every one of these dinners gets 3× more bites when it's served with a story. \"This is Rainbow Rider's favorite dinner.\" \"Broccoli Boomer trained on these skewers.\" This is where <a href=\"/free-buddy\">Plate Buddies</a> comes in — your kid designs their own food character, and suddenly the character eats what they're eating. Copy-paste this trick even without a Buddy: give the meal a name. Give the plate a story. Watch a fork move.",
      },
      {
        type: "cta",
        href: "/free-buddy",
        label: "Design your kid's food character — free",
        sub: "Takes 30 seconds. No credit card needed. Ages 2–7.",
      },
      { type: "h2", text: "What to do when nothing works" },
      {
        type: "p",
        text:
          "Some nights nothing will work — and that's genuinely okay. Serve dinner. Sit down. Eat your food. Try not to hover over their plate. When kids see everyone eating and nobody performing, curiosity has a way of showing up on its own.",
      },
      {
        type: "quote",
        text:
          "You don't have to win every dinner. You just have to keep offering food, calmly, at regular intervals, without turning it into a battle.",
        by: "Ellyn Satter, Division of Responsibility",
      },
      { type: "h2", text: "The 4-week reset" },
      {
        type: "p",
        text:
          "If you'd like to try something structured: for four weeks, cook one thing per meal, skip alternatives, keep comments off how much they eat, and give every dinner a small story. Most families notice a shift by week three. If selective eating persists past a few months of consistent effort, or is paired with weight loss, gagging, or extreme selectivity (fewer than 20 accepted foods), it's worth mentioning to your pediatrician — a feeding therapy consult can be a big help.",
      },
    ],
  },

  {
    slug: "60-second-weekly-meal-plan-for-busy-families",
    title: "The 60-Second Weekly Meal Plan for Busy Families",
    subtitle:
      "You don't need a color-coded spreadsheet or a Sunday afternoon. Here's the actual method we use — 60 seconds, one grocery list, no burnout.",
    excerpt:
      "Skip the Sunday-afternoon spreadsheet. The magnet-board method our founder uses to plan a full week of family dinners in under a minute — and never stare into the fridge again.",
    hero_image:
      "https://images.unsplash.com/photo-1490645935967-10de6ba17061?auto=format&fit=crop&w=1600&q=70",
    hero_alt: "Fridge magnet meal-planning board with a week of dinners",
    category: "meal planning",
    author: "The Plate Buddies Team",
    published_at: "2026-02-18",
    read_time_min: 6,
    tags: ["meal planning", "family dinner", "grocery list", "kids"],
    target_keyword: "kid meal planning",
    body: [
      {
        type: "p",
        text:
          "Almost every parent has tried meal planning. Almost every parent has quit at some point. The problem usually isn't discipline — it's that most meal-planning systems are built for people whose weeks look identical, and very few families with young kids have identical weeks.",
      },
      {
        type: "p",
        text:
          "This is the method our founder uses on weeks that look nothing like the last one. It takes about 60 seconds. It works whether you cook 3 nights a week or 7. And it doesn't need a spreadsheet.",
      },
      { type: "h2", text: "The rule of 5-2" },
      {
        type: "p",
        text:
          "Every family week has, on average, about 5 dinners you'll cook and 2 you won't (leftover night, takeout, at-a-friend's). Trying to plan a perfect 7 is often why plans quietly fall apart. Try planning <b>5 dinners a week</b> and giving yourself two \"flex\" nights guilt-free. That's it.",
      },
      { type: "h2", text: "The 60-second method" },
      {
        type: "num",
        items: [
          "<b>Pick 5 protein rotations.</b> Chicken twice, ground beef once, pasta once, breakfast-for-dinner once. The same five shapes rotate; the sides can change.",
          "<b>Assign each to a day.</b> Monday = pasta, Tuesday = tacos, Wednesday = sheet pan, Thursday = leftovers, Friday = pizza night. Same pattern week after week — your brain stops deciding.",
          "<b>Add one vegetable and one fruit per meal.</b> \"Frozen peas\" counts. \"Bagged spinach\" counts. Simple is the point.",
          "<b>Write the grocery list from the plan, not from the fridge.</b> Standing at the fridge asking \"what do we need?\" is how three opened jars of pesto happen. Let the plan drive the list.",
          "<b>Post it where the family can see it.</b> The magic isn't the plan — it's the plan being visible. Kids ask \"what's for dinner?\" far less when they can just look.",
        ],
      },
      {
        type: "callout",
        tone: "aqua",
        title: "Why the magnet board changes everything",
        text:
          "The reason our physical magnet board and digital Meal Planner exist is because the plan being SEEN is what makes it stick. Your kid asking \"what's for dinner?\" every hour is a trust signal — they don't feel in control. Give them the board.",
      },
      { type: "h2", text: "The 5-2 in practice — one real family week" },
      { type: "h3", text: "Monday · Pasta" },
      {
        type: "p",
        text:
          "Spaghetti + jarred marinara + turkey meatballs (frozen) + a side of frozen peas microwaved with butter. 18 minutes.",
      },
      { type: "h3", text: "Tuesday · Tacos" },
      {
        type: "p",
        text:
          "Ground beef with taco seasoning + tortillas + shredded cheese + corn + diced avocado. 20 minutes. The build-your-own format works for every eater at the table.",
      },
      { type: "h3", text: "Wednesday · Sheet pan" },
      {
        type: "p",
        text:
          "Chicken sausage + baby potatoes + broccoli, tossed with olive oil and salt. 425°F for 22 minutes. Hands-on time: 5 minutes.",
      },
      { type: "h3", text: "Thursday · Leftovers or breakfast-for-dinner" },
      {
        type: "p",
        text:
          "Reheat Wednesday's sheet pan, OR make scrambled eggs + toast + fruit. Nobody complains about breakfast at dinner.",
      },
      { type: "h3", text: "Friday · Pizza night" },
      {
        type: "p",
        text:
          "Naan + jarred sauce + mozzarella + whatever toppings each family member wants. 12 minutes at 450°F. The kid loves it because they built theirs.",
      },
      {
        type: "cta",
        href: "/planner",
        label: "Try the free digital meal planner",
        sub: "Drag and drop your week onto a virtual magnet board. Auto-builds your shopping list.",
      },
      { type: "h2", text: "The 3 mistakes that quietly kill a meal plan" },
      {
        type: "num",
        items: [
          "<b>Planning too much variety.</b> Most families don't secretly want 7 different cuisines a week — they want food they recognize on plates that feel like home. A rotation of 12-15 dinners on repeat is more than enough.",
          "<b>Planning food you've never made.</b> Wednesday might not be the day to try Beef Bourguignon for the first time. Save the ambitious recipes for weekends when there's room to experiment (and forgive).",
          "<b>Planning without a shopping list.</b> The plan stays theoretical until the groceries land in your kitchen. Plan → list → shop, in that order.",
        ],
      },
      { type: "h2", text: "The lazy Sunday version" },
      {
        type: "p",
        text:
          "If Sunday planning feels impossible, do it Saturday morning in pajamas while the kids watch cartoons. Sixty seconds. Assign 5 protein rotations to 5 days. Text the list to yourself or your grocery app. Get back in bed.",
      },
      { type: "h2", text: "What makes this stick" },
      {
        type: "p",
        text:
          "The plan works because you've quietly removed decisions, not because you've added discipline. Discipline is exhausting — \"Tuesday is taco night\" isn't. That's the whole gentle trick.",
      },
    ],
  },

  {
    slug: "screen-free-dinner-ideas-for-family-night",
    title: "5 Screen-Free Ways to Make Family Dinner Fun Again",
    subtitle:
      "You already know screens at dinner are hurting connection. Here's what to do instead — five zero-prep games and rituals that turn dinner into the best 20 minutes of everyone's day.",
    excerpt:
      "Zero-prep screen-free dinner rituals that actually work with a 3-year-old at the table. Rose-Bud-Thorn, Would You Rather, and 3 more games that beat any tablet.",
    hero_image:
      "https://images.unsplash.com/photo-1529543544282-ea669407fca3?auto=format&fit=crop&w=1600&q=70",
    hero_alt: "Family sitting together at dinner laughing without screens",
    category: "screen-free",
    author: "The Plate Buddies Team",
    published_at: "2026-02-10",
    read_time_min: 5,
    tags: ["screen-free", "family dinner", "connection", "kids"],
    target_keyword: "screen-free family dinners",
    body: [
      {
        type: "p",
        text:
          "The best predictor of a kid's future wellbeing isn't their school, their sports, or their reading level — it's how many meals they share with their family. That's the research. And here's the softer part that doesn't get said as often: <b>a dinner with everyone on a screen doesn't quite count the same way.</b>",
      },
      {
        type: "p",
        text:
          "Nobody's going to shame anyone out of scrolling (we do it too). What can help is making the 20 minutes at the table more interesting than the phone. That's what these five rituals are for.",
      },
      {
        type: "callout",
        tone: "sun",
        title: "The one gentle rule",
        text:
          "Phones live face-down in a basket during dinner — grown-ups included. Kids mirror what they see far more than what they hear, and this one's on all of us.",
      },
      { type: "h2", text: "1. Rose · Bud · Thorn (ages 3+)" },
      {
        type: "p",
        text:
          "Everyone at the table shares three things about their day: a <b>Rose</b> (something good), a <b>Bud</b> (something they're looking forward to), and a <b>Thorn</b> (something hard). Takes 3 minutes. Works on a 3-year-old. Works on a teenager. Works on you.",
      },
      {
        type: "p",
        text:
          "The magic is the Thorn. Kids need a low-stakes way to name what hurt today. Ask often when they're small, and there's a good chance they'll still tell you at twelve.",
      },
      { type: "h2", text: "2. Would You Rather (ages 4+)" },
      {
        type: "p",
        text:
          "One person asks: \"Would you rather have chicken feet or fish eyes?\" Everyone answers with a reason. Next person. That's it. Never ends. Never gets old.",
      },
      {
        type: "p",
        text:
          "Starter prompts:",
      },
      {
        type: "list",
        items: [
          "Would you rather be able to fly OR be invisible?",
          "Would you rather live inside a chocolate cake OR a giant pineapple?",
          "Would you rather have 10 hands OR 10 feet?",
          "Would you rather be a superhero with no powers OR a regular kid with one weird power?",
        ],
      },
      { type: "h2", text: "3. The Buddy Story (ages 2+)" },
      {
        type: "p",
        text:
          "This is the one that unlocks the toddler at your table. Give the meal a character. \"This is Broccoli Boomer's favorite dinner — do you know why he's brave enough to eat spicy stuff?\" Then let the kid tell you. Tomorrow: \"What adventure is Buddy on tonight?\"",
      },
      {
        type: "p",
        text:
          "You don't need our tools to do this. You can invent a character on the spot. But if you want it to stick, <a href=\"/free-buddy\">design your kid their own food Buddy for free</a>. It becomes theirs. And their Buddy has opinions about vegetables.",
      },
      {
        type: "cta",
        href: "/free-buddy",
        label: "Design a Plate Buddy — free",
        sub: "Your kid's own food character. Ages 2–7. 30 seconds. No credit card.",
      },
      { type: "h2", text: "4. High-Low-Hero (ages 5+)" },
      {
        type: "p",
        text:
          "Each person names the <b>high</b> of their day, the <b>low</b>, and the <b>hero</b> — one person who was kind or helpful. The Hero part is the whole point: you're training your kid to notice who's kind to them, and to say it out loud. Also great for parents to hear each other named.",
      },
      { type: "h2", text: "5. Kitchen Compliments (ages 2+)" },
      {
        type: "p",
        text:
          "Once a week — Fridays work — go around the table and each person gives the cook one specific compliment about the meal. \"The rice is fluffy.\" \"The sauce is my favorite orange.\" \"You made the chicken not sad.\" Toddler compliments are the best compliments.",
      },
      {
        type: "p",
        text:
          "Then next week, whoever cooked picks the next chef — even if the next chef is a 4-year-old who's going to \"cook\" cheese-and-crackers night. Let it happen. That's a memory.",
      },
      { type: "h2", text: "What to do when it flops" },
      {
        type: "p",
        text:
          "Some nights these games will fall flat — that's normal, and it doesn't mean you did it wrong. Say \"we'll try again tomorrow\" and eat quietly. The consistency of trying matters far more than any single dinner going well.",
      },
      {
        type: "quote",
        text:
          "The kids who talk to their parents at 15 are the kids whose parents kept talking to them at 5 — even when the answers were nonsense.",
      },
      { type: "h2", text: "The 20-minute rule" },
      {
        type: "p",
        text:
          "You don't need a two-hour dinner. Twenty minutes at the table, phones down, one ritual, everyone eating together — that's the whole gentle thing. Do it four nights a week for a year and it adds up more than you'd think.",
      },
    ],
  },
];

/** Ordered newest → oldest for display. */
export const PUBLISHED_POSTS = [...POSTS].sort(
  (a, b) => new Date(b.published_at) - new Date(a.published_at),
);

export function findPostBySlug(slug) {
  return POSTS.find((p) => p.slug === slug);
}

export function getCategoryColor(category) {
  return (
    {
      "food exploration": "#F26A34",
      "meal planning": "#7CC5D4",
      "screen-free": "#FFC15E",
    }[category] || "#F26A34"
  );
}
